import { AnalysisResult, CoreAnalysisEngine } from "./coreAnalysisEngine";
import { ReactElement } from "react";
import { ResultSl1 } from "../components/molecules/ResultSl1";
import { AnalysisEngineType, EngineType } from "../types";
import { formatApiResponse } from "../utils/formatApiResponse";
import type { components } from "../types/voislog_api";

export type Sl1AnalysisResult = AnalysisResult<"sl1">;

export type NinetyDayAverage = components["schemas"]["Sl1NinetyDayAverage"];

type SoundKey = "p6" | "p7" | "p8" | "p9";

type Sl1SoundDetail = {
  speech_reliability_flag: boolean;
  speech_reliability_score: number;
  wav_sample_freq: number;
  details_reliability: {
    phoneme_detected: string;
    reliable_onset_sec: number;
    reliable_offset_sec: number;
  };
};

type Sl1BinaryModel = {
  score: number;
};

type Sl1MultiModel = {
  score: Record<string, number>;
};

type Sl1DetailedAnalysisResult = {
  details_sound: Record<SoundKey, Sl1SoundDetail>;
  details_binary: {
    decision1_binaryclass: string;
  } & Record<SoundKey, Record<string, Sl1BinaryModel>>;
  details_multiclass: {
    decision2_multiclass: string;
  } & Record<SoundKey, Record<string, Sl1MultiModel>>;
  ninety_day_average_past_data: PastDataItem[];
};

// ★ PhonemeDetected は文字列、true/false は IsReliable に
export type ReliabilityScore = {
  IsReliable: boolean | string; // speech_reliability_flag
  ReliableScore: number | string; // speech_reliability_score
  PhonemeDetected: string; // ",[aiueo]," 等
  ReliableOnsetSec: number | string;
  ReliableOffsetSec: number | string;
};

export type MultiClassScore = {
  Healthy: number | string;
  Mild: number | string;
  Mod: number | string;
  Sev: number | string;
};

export type PastDataItem = {
  id: number;
  recording_id: string;
  analyzed_at: string;
  analysis_result: components["schemas"]["Sl1AnalysisResult"];
};

export type ConvertResultType = {
  Decision: string;
  SleepQualityScore: number | string;
  CurrentSleepQualityScore: number | string;
  NinetyDayAverage: NinetyDayAverage | null;
  PastData: PastDataItem[];

  // 信頼性スコア
  ColAReliabilityScore: ReliabilityScore;
  ColPaReliabilityScore: ReliabilityScore;
  ColGaReliabilityScore: ReliabilityScore;
  ColRaReliabilityScore: ReliabilityScore;

  // 二群判定
  DecisionBinaryClass: string;
  ColABinaryScore: number | string;
  ColPaBinaryScore: number | string;
  ColGaBinaryScore: number | string;
  ColRaBinaryScore: number | string;

  // 四群判定
  DecisionMultiClass: string;
  ColAMultiScore: MultiClassScore;
  ColPaMultiScore: MultiClassScore;
  ColGaMultiScore: MultiClassScore;
  ColRaMultiScore: MultiClassScore;

  // サンプリングレート
  ColASampleFreq: number | string;
  ColPaSampleFreq: number | string;
  ColGaSampleFreq: number | string;
  ColRaSampleFreq: number | string;
};

export function ConvertResult({
  result,
}: {
  result: Sl1AnalysisResult | null;
}): ConvertResultType {
  const emptyReliability = (): ReliabilityScore => ({
    IsReliable: "-",
    ReliableScore: "-",
    PhonemeDetected: "-",
    ReliableOnsetSec: "-",
    ReliableOffsetSec: "-",
  });

  const emptyMulti = (): MultiClassScore => ({
    Healthy: "-",
    Mild: "-",
    Mod: "-",
    Sev: "-",
  });

  const resultParams: ConvertResultType = {
    Decision: "-",
    SleepQualityScore: "-",
    CurrentSleepQualityScore: "-",
    NinetyDayAverage: null,
    PastData: [],

    // サンプリングレート
    ColASampleFreq: "-",
    ColPaSampleFreq: "-",
    ColGaSampleFreq: "-",
    ColRaSampleFreq: "-",

    // 信頼性スコア
    ColAReliabilityScore: emptyReliability(),
    ColPaReliabilityScore: emptyReliability(),
    ColGaReliabilityScore: emptyReliability(),
    ColRaReliabilityScore: emptyReliability(),

    // 二群判定
    DecisionBinaryClass: "-",
    ColABinaryScore: "-",
    ColPaBinaryScore: "-",
    ColGaBinaryScore: "-",
    ColRaBinaryScore: "-",

    // 四群判定
    DecisionMultiClass: "-",
    ColAMultiScore: emptyMulti(),
    ColPaMultiScore: emptyMulti(),
    ColGaMultiScore: emptyMulti(),
    ColRaMultiScore: emptyMulti(),
  };

  if (!result) return resultParams;

  const detailed = result.detailed_analysis_result as
    Sl1DetailedAnalysisResult | undefined;
  if (!detailed) return resultParams;

  const sound = detailed.details_sound;
  const multi = detailed.details_multiclass;
  const binary = detailed.details_binary;

  // ヘッダ
  resultParams.Decision = result.analysis_result.decision;
  resultParams.CurrentSleepQualityScore = formatApiResponse(
    result.analysis_result.sleep_quality_score * 100,
  );

  // 90日間平均と履歴データ
  resultParams.NinetyDayAverage = result.analysis_result.ninety_day_average;
  resultParams.SleepQualityScore =
    Math.round(
      result.analysis_result.ninety_day_average.sleep_quality_score * 1000,
    ) / 10;
  resultParams.PastData = detailed.ninety_day_average_past_data;

  // 決定クラス（全体）
  const binaryDecision = binary.decision1_binaryclass;
  const multiDecision = multi.decision2_multiclass;
  resultParams.DecisionBinaryClass = binaryDecision;
  resultParams.DecisionMultiClass = multiDecision;

  // 信頼性 & サンプルレート
  const buildReliability = (p: SoundKey): ReliabilityScore => {
    const s = sound[p];
    const reli = s.details_reliability;
    return {
      IsReliable: s.speech_reliability_flag,
      ReliableScore: formatApiResponse(
        s.speech_reliability_score,
        undefined,
        true,
      ),
      PhonemeDetected: reli.phoneme_detected,
      ReliableOnsetSec: reli.reliable_onset_sec,
      ReliableOffsetSec: reli.reliable_offset_sec,
    };
  };

  resultParams.ColAReliabilityScore = buildReliability("p6");
  resultParams.ColGaReliabilityScore = buildReliability("p7");
  resultParams.ColRaReliabilityScore = buildReliability("p8");
  resultParams.ColPaReliabilityScore = buildReliability("p9");

  resultParams.ColASampleFreq = formatApiResponse(sound.p6.wav_sample_freq, 1);
  resultParams.ColGaSampleFreq = formatApiResponse(sound.p7.wav_sample_freq, 1);
  resultParams.ColRaSampleFreq = formatApiResponse(sound.p8.wav_sample_freq, 1);
  resultParams.ColPaSampleFreq = formatApiResponse(sound.p9.wav_sample_freq, 1);

  // 二群（binary）：選ばれたクラスの確率を返す
  // モデルのscoreは "sub1_2modOR3sev"(陽性側) の確率という前提
  const binScore = (p: SoundKey): number => binary[p][`model_${p}_2cls`].score;
  const pickBinaryProb = (s: number): number =>
    binaryDecision === "sub1_2modOR3sev" ? s : 1 - s;

  resultParams.ColABinaryScore = formatApiResponse(
    pickBinaryProb(binScore("p6")),
    10,
  );
  resultParams.ColGaBinaryScore = formatApiResponse(
    pickBinaryProb(binScore("p7")),
    10,
  );
  resultParams.ColRaBinaryScore = formatApiResponse(
    pickBinaryProb(binScore("p8")),
    10,
  );
  resultParams.ColPaBinaryScore = formatApiResponse(
    pickBinaryProb(binScore("p9")),
    10,
  );

  // 四群（multiclass）：4クラスすべて埋める
  const mkeys = {
    Healthy: "sub2_0he",
    Mild: "sub2_1mild",
    Mod: "sub2_2mod",
    Sev: "sub2_3sev",
  } as const;

  const buildMultiScore = (p: SoundKey): MultiClassScore => {
    const scores = multi[p][`model_${p}_4cls`].score;
    return {
      Healthy: formatApiResponse(scores[mkeys.Healthy], 10),
      Mild: formatApiResponse(scores[mkeys.Mild], 10),
      Mod: formatApiResponse(scores[mkeys.Mod], 10),
      Sev: formatApiResponse(scores[mkeys.Sev], 10),
    };
  };

  resultParams.ColAMultiScore = buildMultiScore("p6");
  resultParams.ColGaMultiScore = buildMultiScore("p7");
  resultParams.ColRaMultiScore = buildMultiScore("p8");
  resultParams.ColPaMultiScore = buildMultiScore("p9");

  return resultParams;
}

interface EngineProps {
  _analysisType?: AnalysisEngineType | null;
}

export class Sl1Engine extends CoreAnalysisEngine<"sl1"> {
  _engineType: EngineType = "sl1";
  _analysisType: AnalysisEngineType | null = null;

  constructor(props?: EngineProps) {
    super("sl1");
    if (props && props._analysisType) {
      this._analysisType = props._analysisType;
    }
  }

  async renderResult(key: number, fileId: string): Promise<ReactElement> {
    const result = await this.getResult(fileId);
    return <ResultSl1 key={key} sl1Result={result} />;
  }
}
